import React, { ReactNode, useState, useEffect } from "react";
import { getCameraService } from "../../services/cameraService";

interface MapContainerProps {
  children: ReactNode;
}

export function MapContainer({ children }: MapContainerProps) {
  const [cameraLayout, setCameraLayout] = useState<
    "regular" | "ultrawide" | "full" | "full-wide"
  >("regular");
  const [cameraEnabled, setCameraEnabled] = useState(false);
  const [isPortrait, setIsPortrait] = useState(
    window.innerHeight > window.innerWidth,
  );

  useEffect(() => {
    const cameraService = getCameraService();

    // Check camera state periodically
    const checkCameraState = () => {
      const settings = cameraService.getSettings();
      const active = cameraService.isActive();
      setCameraLayout(settings.layout);
      setCameraEnabled(active);
    };

    checkCameraState();
    const interval = setInterval(checkCameraState, 500);

    // Listen for orientation changes
    const handleResize = () => {
      setIsPortrait(window.innerHeight > window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      clearInterval(interval);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  // When camera is in fullscreen mode (full or full-wide), shrink map to top-right corner
  const isFullScreenCamera =
    cameraEnabled && (cameraLayout === "full" || cameraLayout === "full-wide");

  return (
    <div
      className="map-container"
      style={
        isFullScreenCamera
          ? {
              position: "absolute",
              top: "0",
              right: "0",
              left: "auto",
              bottom: "auto",
              width: isPortrait ? "100vw" : "min(320px, 90vw)",
              height: "auto",
              maxHeight: isPortrait ? "35vh" : "50vh", // Shorter in portrait
              aspectRatio: "4 / 3",
              zIndex: 3,
              borderBottomLeftRadius: "20px",
              overflow: "hidden",
              // CSS mask: fade bottom and left in landscape, only bottom in portrait
              WebkitMaskImage: isPortrait
                ? "linear-gradient(to bottom, black 70%, transparent 100%)"
                : "linear-gradient(to bottom, black 70%, transparent 100%), linear-gradient(to right, transparent 0%, black 15%)",
              maskImage: isPortrait
                ? "linear-gradient(to bottom, black 70%, transparent 100%)"
                : "linear-gradient(to bottom, black 70%, transparent 100%), linear-gradient(to right, transparent 0%, black 15%)",
              WebkitMaskComposite: isPortrait ? undefined : "source-in",
              maskComposite: isPortrait ? undefined : "intersect",
            }
          : undefined
      }
    >
      {children}
    </div>
  );
}
